import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Spinner } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import Icon from '../../common/Icons';
import MemberService from '../../../services/memberService';
import { parseDebaucheFile } from './parseDebaucheFile';
import { downloadDebaucheTemplate } from './downloadDebaucheTemplate';
import '../import-dns/ImportDNSView.css';

const ITEMS_PER_PAGE = 20;

const PREVIEW_FILTERS = [
  { id: 'all', label: 'Toutes les lignes' },
  { id: 'actionable', label: 'À débaucher' },
  { id: 'issues', label: 'Problèmes' }
];

const STATUS_META = {
  AGENT: { label: 'Agent (foyer)', tone: 'ok' },
  DEPENDENT: { label: 'Ayant-droit', tone: 'ok' },
  ALREADY_ARCHIVED: { label: 'Déjà archivé', tone: 'warn' },
  NOT_FOUND: { label: 'Introuvable', tone: 'bad' },
  AMBIGUOUS: { label: 'Nom ambigu', tone: 'bad' },
  DUPLICATE: { label: 'Doublon fichier', tone: 'warn' }
};

const isActionable = (item) => item.status === 'AGENT' || item.status === 'DEPENDENT';
const isIssue = (item) => !isActionable(item);

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} o`;
  return `${(bytes / 1024).toFixed(1)} Ko`;
}

/**
 * Assistant de débauche en masse par fichier Excel :
 * société → fichier de matricules → aperçu résolu → confirmation.
 */
const ImportDebaucheView = ({ companies = [], showToast, onImportSuccess, variant = 'personnel' }) => {
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [companySearchQuery, setCompanySearchQuery] = useState('');
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewPage, setPreviewPage] = useState(1);
  const [previewFilter, setPreviewFilter] = useState('all');
  const [reason, setReason] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [importing, setImporting] = useState(false);

  const fileInputRef = useRef(null);
  const companyWrapRef = useRef(null);
  const companySearchRef = useRef(null);
  const previewRef = useRef(null);
  const [companyDropdownPos, setCompanyDropdownPos] = useState(null);

  const rootClass = `dns-import dns-import--${variant} dns-import--debauche`;

  const updateCompanyDropdownPos = useCallback(() => {
    const el = companySearchRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setCompanyDropdownPos({
      top: rect.bottom + 6,
      left: rect.left,
      width: Math.max(rect.width, 220)
    });
  }, []);

  useEffect(() => {
    if (!showCompanyDropdown) {
      setCompanyDropdownPos(null);
      return undefined;
    }
    updateCompanyDropdownPos();
    window.addEventListener('resize', updateCompanyDropdownPos);
    window.addEventListener('scroll', updateCompanyDropdownPos, true);
    return () => {
      window.removeEventListener('resize', updateCompanyDropdownPos);
      window.removeEventListener('scroll', updateCompanyDropdownPos, true);
    };
  }, [showCompanyDropdown, companySearchQuery, updateCompanyDropdownPos]);

  useEffect(() => {
    const onDocClick = (e) => {
      const inField = companyWrapRef.current?.contains(e.target);
      const inList = e.target.closest?.('.dns-import-company-list');
      if (!inField && !inList) {
        setShowCompanyDropdown(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const runPreview = useCallback(
    async (companyId, rows) => {
      const response = await MemberService.previewArchiveImport({
        company_id: parseInt(companyId, 10),
        entries: rows.map((r) => ({
          matricule: r.matricule || '',
          nom: r.nom || '',
          prenom: r.prenom || '',
          rowNumber: r.rowNumber
        }))
      });
      return response?.data || { items: [], summary: {} };
    },
    []
  );

  const handleFileChange = useCallback(
    (selectedFile) => {
      if (!selectedFile) return;
      if (!selectedCompanyId) {
        showToast("Veuillez d'abord sélectionner une société", 'danger');
        return;
      }

      setParsing(true);
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const dataBuf = new Uint8Array(e.target.result);
          const workbook = XLSX.read(dataBuf, { type: 'array' });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });

          const { rows } = parseDebaucheFile(jsonData);
          const data = await runPreview(selectedCompanyId, rows);

          setPreview(data);
          setFile(selectedFile);
          setPreviewPage(1);
          setPreviewFilter('all');

          requestAnimationFrame(() => {
            previewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          });

          const s = data.summary || {};
          const ready = (s.agents || 0) + (s.dependents || 0);
          showToast(
            `${s.total || rows.length} matricule(s) analysé(s) — ${ready} à débaucher.`,
            ready > 0 ? 'success' : 'warning'
          );
        } catch (err) {
          showToast(`Erreur lors de l'analyse : ${err.message}`, 'danger');
          setPreview(null);
          setFile(null);
        } finally {
          setParsing(false);
        }
      };

      reader.onerror = () => {
        showToast('Erreur lors de la lecture du fichier', 'danger');
        setParsing(false);
      };

      reader.readAsArrayBuffer(selectedFile);
    },
    [selectedCompanyId, runPreview, showToast]
  );

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!selectedCompanyId) return;
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (!selectedCompanyId) {
      showToast("Veuillez d'abord sélectionner une société", 'danger');
      return;
    }
    if (e.dataTransfer.files?.[0]) handleFileChange(e.dataTransfer.files[0]);
  };

  const clearFile = () => {
    setFile(null);
    setPreview(null);
    setPreviewPage(1);
    setPreviewFilter('all');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const resetAll = () => {
    clearFile();
    setReason('');
    setSelectedCompanyId('');
    setCompanySearchQuery('');
  };

  const handleDownloadTemplate = async () => {
    try {
      await downloadDebaucheTemplate();
      showToast('Modèle de débauche téléchargé.', 'success');
    } catch {
      showToast('Impossible de télécharger le modèle.', 'danger');
    }
  };

  const handleImport = async () => {
    if (!preview || !selectedCompanyId) return;
    const memberIds = (preview.items || [])
      .filter(isActionable)
      .map((i) => i.member?.id)
      .filter(Boolean);
    if (memberIds.length === 0) {
      showToast('Aucun adhérent valide à débaucher.', 'danger');
      return;
    }

    try {
      setImporting(true);
      const response = await MemberService.runArchiveImport({
        company_id: parseInt(selectedCompanyId, 10),
        member_ids: memberIds,
        reason: reason.trim() || undefined
      });
      showToast(response.message || 'Débauche en masse effectuée.', 'success');
      setTimeout(() => {
        resetAll();
        if (typeof onImportSuccess === 'function') onImportSuccess();
      }, 1400);
    } catch (err) {
      showToast(err.message || 'Erreur lors de la débauche', 'danger');
    } finally {
      setImporting(false);
    }
  };

  const selectedCompany = companies.find((c) => c.id === parseInt(selectedCompanyId, 10));
  const summary = preview?.summary || {};

  const filteredCompanies = companies.filter((c) => {
    const q = companySearchQuery.toLowerCase().trim();
    if (!q) return true;
    return (
      c.name?.toLowerCase().includes(q) ||
      c.company_code?.toLowerCase().includes(q)
    );
  });

  const filteredItems = useMemo(() => {
    const items = preview?.items || [];
    if (previewFilter === 'actionable') return items.filter(isActionable);
    if (previewFilter === 'issues') return items.filter(isIssue);
    return items;
  }, [preview, previewFilter]);

  useEffect(() => {
    setPreviewPage(1);
  }, [previewFilter]);

  const totalPreviewPages = filteredItems.length
    ? Math.ceil(filteredItems.length / ITEMS_PER_PAGE)
    : 0;
  const previewStart = (previewPage - 1) * ITEMS_PER_PAGE;
  const currentPageItems = filteredItems.slice(previewStart, previewStart + ITEMS_PER_PAGE);

  const actionableCount = (summary.agents || 0) + (summary.dependents || 0);
  const step1Done = Boolean(selectedCompanyId);
  const step2Done = Boolean(file && preview);
  const canConfirm = actionableCount > 0 && !importing;

  return (
    <>
      <div className={rootClass}>
        <header className="dns-import-hero">
          <div className="dns-import-hero__main">
            <div className="dns-import-hero__icon" aria-hidden>
              <Icon name="inbox" size={24} />
            </div>
            <div>
              <h2 className="dns-import-hero__title">Débauche en masse (Excel)</h2>
              <p className="dns-import-hero__desc">
                Chargez un fichier de matricules à débaucher. Les agents archivent
                automatiquement tout leur foyer. Aucune suppression : tout est réintégrable
                depuis les archives.
              </p>
              <ul className="dns-import-hero__chips" aria-label="Informations">
                <li>.xlsx · .xls · .csv</li>
                <li>Aperçu obligatoire</li>
                <li>Réintégration possible</li>
              </ul>
            </div>
          </div>
          <button
            type="button"
            className="dns-import-hero__template-btn"
            onClick={handleDownloadTemplate}
          >
            <Icon name="download" size={16} />
            Télécharger le modèle
          </button>
        </header>

        <nav className="dns-import-steps" aria-label="Étapes de la débauche">
          <div className={`dns-import-step ${step1Done ? 'is-done' : 'is-active'}`}>
            <span className="dns-import-step__badge">{step1Done ? '✓' : '1'}</span>
            <span className="dns-import-step__text">Société</span>
          </div>
          <div
            className={`dns-import-step ${
              step1Done && !step2Done ? 'is-active' : step2Done ? 'is-done' : ''
            }`}
          >
            <span className="dns-import-step__badge">{step2Done ? '✓' : '2'}</span>
            <span className="dns-import-step__text">Fichier</span>
          </div>
          <div className={`dns-import-step ${step2Done ? 'is-active' : ''}`}>
            <span className="dns-import-step__badge">3</span>
            <span className="dns-import-step__text">Validation</span>
          </div>
        </nav>

        <div className={`dns-import-grid ${step2Done ? 'has-preview' : ''}`}>
          <aside className="dns-import-setup">
            <section
              className={`dns-import-card ${step1Done ? 'is-complete' : 'is-current'}`}
              aria-labelledby="deb-step-company"
            >
              <div className="dns-import-card__head">
                <span className="dns-import-card__step">1</span>
                <div>
                  <h3 id="deb-step-company">Société cible</h3>
                  <p>Les matricules seront résolus dans cette société</p>
                </div>
              </div>
              <div className="dns-import-card__body">
                <div className="dns-import-company" ref={companyWrapRef}>
                  <label className="dns-import-field-label" htmlFor="deb-company-search">
                    Rechercher
                  </label>
                  <div className="dns-import-company-field">
                    <div className="dns-import-search" ref={companySearchRef}>
                      <Icon name="search" size={16} aria-hidden />
                      <input
                        id="deb-company-search"
                        type="search"
                        placeholder="Nom ou code société…"
                        value={companySearchQuery}
                        onChange={(e) => {
                          setCompanySearchQuery(e.target.value);
                          setShowCompanyDropdown(true);
                        }}
                        onFocus={() => setShowCompanyDropdown(true)}
                        autoComplete="off"
                        role="combobox"
                        aria-expanded={showCompanyDropdown}
                        aria-controls="deb-company-listbox"
                        aria-autocomplete="list"
                      />
                    </div>
                  </div>
                  {showCompanyDropdown &&
                    companyDropdownPos &&
                    typeof document !== 'undefined' &&
                    createPortal(
                      <ul
                        id="deb-company-listbox"
                        className="dns-import-company-list dns-import-company-list--portal"
                        role="listbox"
                        style={{
                          position: 'fixed',
                          top: companyDropdownPos.top,
                          left: companyDropdownPos.left,
                          width: companyDropdownPos.width,
                          zIndex: 1500
                        }}
                      >
                        {filteredCompanies.length > 0 ? (
                          filteredCompanies.map((company) => (
                            <li key={company.id} role="none">
                              <button
                                type="button"
                                role="option"
                                aria-selected={selectedCompanyId === String(company.id)}
                                className={`dns-import-company-option ${
                                  selectedCompanyId === String(company.id) ? 'is-selected' : ''
                                }`}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                  setSelectedCompanyId(String(company.id));
                                  setShowCompanyDropdown(false);
                                  setCompanySearchQuery('');
                                  clearFile();
                                }}
                              >
                                <span className="dns-import-company-option__code">
                                  {company.company_code || '—'}
                                </span>
                                <span className="dns-import-company-option__name">
                                  {company.name}
                                </span>
                              </button>
                            </li>
                          ))
                        ) : (
                          <li className="dns-import-company-list__empty">Aucune société trouvée</li>
                        )}
                      </ul>,
                      document.body
                    )}
                </div>
                {selectedCompany && (
                  <div className="dns-import-pill dns-import-pill--success" style={{ marginTop: 12 }}>
                    <Icon name="checkCircle" size={16} />
                    <span>
                      <strong>{selectedCompany.company_code || '—'}</strong> — {selectedCompany.name}
                    </span>
                  </div>
                )}
              </div>
            </section>

            <section
              className={`dns-import-card ${!step1Done ? 'is-locked' : step2Done ? 'is-complete' : 'is-current'}`}
              aria-labelledby="deb-step-file"
            >
              <div className="dns-import-card__head">
                <span className="dns-import-card__step">2</span>
                <div>
                  <h3 id="deb-step-file">Fichier des matricules</h3>
                  <p>
                    {step1Done
                      ? 'Glissez-déposez ou sélectionnez votre fichier'
                      : 'Choisissez d’abord une société'}
                  </p>
                </div>
              </div>
              <div className="dns-import-card__body">
                <div
                  className={`dns-import-dropzone ${dragActive ? 'is-drag' : ''} ${
                    !step1Done ? 'is-disabled' : ''
                  } ${parsing ? 'is-loading' : ''}`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  {parsing ? (
                    <div className="dns-import-dropzone__loading">
                      <Spinner animation="border" size="sm" variant="primary" />
                      <p>Analyse du fichier en cours…</p>
                    </div>
                  ) : (
                    <>
                      <div className="dns-import-dropzone__icon">
                        <Icon name="cloudUpload" size={32} />
                      </div>
                      <p className="dns-import-dropzone__title">Déposer le fichier ici</p>
                      <p className="dns-import-dropzone__hint">ou parcourir depuis votre poste</p>
                      <input
                        ref={fileInputRef}
                        id="deb-file-input"
                        type="file"
                        className="dns-import-file-input"
                        accept=".xlsx,.xls,.csv"
                        disabled={!step1Done || parsing}
                        onChange={(e) => handleFileChange(e.target.files?.[0])}
                      />
                      <label htmlFor="deb-file-input" className="dns-import-browse">
                        Parcourir les fichiers
                      </label>
                    </>
                  )}
                </div>

                {file && !parsing && (
                  <div className="dns-import-file">
                    <div className="dns-import-file__icon">
                      <Icon name="fileExcel" size={18} />
                    </div>
                    <div className="dns-import-file__meta">
                      <span className="dns-import-file__name">{file.name}</span>
                      <span className="dns-import-file__sub">
                        {formatFileSize(file.size)} ·{' '}
                        {new Date(file.lastModified).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                    <button type="button" className="dns-import-link-btn" onClick={clearFile}>
                      Changer
                    </button>
                  </div>
                )}

                {step1Done && (
                  <div style={{ marginTop: 14 }}>
                    <label className="dns-import-field-label" htmlFor="deb-reason">
                      Motif global (optionnel)
                    </label>
                    <textarea
                      id="deb-reason"
                      rows={2}
                      maxLength={255}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Ex. : plan social, fin de marché…"
                      style={{
                        width: '100%',
                        padding: '0.55rem 0.7rem',
                        borderRadius: 9,
                        border: '1px solid rgba(15,23,42,0.15)',
                        resize: 'vertical'
                      }}
                    />
                  </div>
                )}
              </div>
            </section>
          </aside>

          <main className="dns-import-preview" ref={previewRef}>
            {!preview ? (
              <div className="dns-import-preview-empty">
                <div className="dns-import-preview-empty__icon">
                  <Icon name="inbox" size={32} />
                </div>
                <h3>Aperçu de la débauche</h3>
                <p>
                  Une fois le fichier analysé, vous verrez ici quels matricules seront
                  débauchés, lesquels sont introuvables ou déjà archivés, avant confirmation.
                </p>
              </div>
            ) : (
              <section className="dns-import-card dns-import-card--preview is-current">
                <div className="dns-import-card__head">
                  <span className="dns-import-card__step">3</span>
                  <div>
                    <h3>Vérification & débauche</h3>
                    <p>Contrôlez les correspondances puis confirmez</p>
                  </div>
                  <span className={`dns-import-ready-badge ${actionableCount === 0 ? 'has-error' : ''}`}>
                    {actionableCount}/{summary.total || 0} prêts
                  </span>
                </div>

                <div className="dns-import-card__body">
                  <div className="dns-import-kpis" aria-live="polite">
                    <div className="dns-import-kpi dns-import-kpi--agent">
                      <span className="dns-import-kpi__value">{summary.agents || 0}</span>
                      <span className="dns-import-kpi__label">Agents</span>
                    </div>
                    <div className="dns-import-kpi dns-import-kpi--spouse">
                      <span className="dns-import-kpi__value">{summary.foyerDependents || 0}</span>
                      <span className="dns-import-kpi__label">Ayants-droit (foyers)</span>
                    </div>
                    <div className="dns-import-kpi dns-import-kpi--child">
                      <span className="dns-import-kpi__value">{summary.dependents || 0}</span>
                      <span className="dns-import-kpi__label">Ayants-droit ciblés</span>
                    </div>
                    <div className="dns-import-kpi dns-import-kpi--warn">
                      <span className="dns-import-kpi__value">{summary.alreadyArchived || 0}</span>
                      <span className="dns-import-kpi__label">Déjà archivés</span>
                    </div>
                    <div className="dns-import-kpi dns-import-kpi--warn">
                      <span className="dns-import-kpi__value">{summary.notFound || 0}</span>
                      <span className="dns-import-kpi__label">Introuvables</span>
                    </div>
                  </div>

                  <div className="dns-import-toolbar">
                    <div className="dns-import-segment" role="group" aria-label="Filtre aperçu">
                      {PREVIEW_FILTERS.map((f) => (
                        <button
                          key={f.id}
                          type="button"
                          aria-pressed={previewFilter === f.id}
                          className={`dns-import-segment__btn ${previewFilter === f.id ? 'is-active' : ''}`}
                          onClick={() => setPreviewFilter(f.id)}
                        >
                          {f.label}
                        </button>
                      ))}
                    </div>
                    <span className="dns-import-toolbar__meta">
                      {filteredItems.length} ligne(s) affichée(s)
                    </span>
                  </div>

                  <div className="dns-import-table-scroll">
                    <table className="dns-import-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Matricule</th>
                          <th>Adhérent</th>
                          <th>Type</th>
                          <th>Statut</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentPageItems.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="dns-import-table__empty">
                              Aucune ligne pour ce filtre
                            </td>
                          </tr>
                        ) : (
                          currentPageItems.map((item, idx) => {
                            const meta = STATUS_META[item.status] || { label: item.status, tone: 'warn' };
                            const fullName = item.member
                              ? `${item.member.nom || ''} ${item.member.prenom || ''}`.trim()
                              : '';
                            return (
                              <tr
                                key={`${item.matricule}-${idx}`}
                                className={meta.tone === 'bad' ? 'is-error' : ''}
                              >
                                <td className="text-muted">{previewStart + idx + 1}</td>
                                <td>
                                  <code className="dns-import-mat">{item.matricule}</code>
                                </td>
                                <td>
                                  {fullName || <span className="text-muted">—</span>}
                                  {item.status === 'AGENT' && item.member?.dependent_count > 0 && (
                                    <span className="dns-import-tag">
                                      +{item.member.dependent_count} foyer
                                    </span>
                                  )}
                                </td>
                                <td>{item.member?.type || '—'}</td>
                                <td>
                                  <span
                                    className={`dns-import-link dns-import-link--${
                                      meta.tone === 'ok' ? 'ok' : 'bad'
                                    }`}
                                  >
                                    <Icon
                                      name={meta.tone === 'ok' ? 'check' : 'alertTriangle'}
                                      size={12}
                                    />
                                    {meta.label}
                                  </span>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>

                  {filteredItems.length > ITEMS_PER_PAGE && (
                    <footer className="dns-import-pagination">
                      <span>
                        {previewStart + 1}–
                        {Math.min(previewStart + ITEMS_PER_PAGE, filteredItems.length)} sur{' '}
                        {filteredItems.length}
                      </span>
                      <nav aria-label="Pagination aperçu">
                        <button
                          type="button"
                          disabled={previewPage <= 1}
                          onClick={() => setPreviewPage((p) => p - 1)}
                          aria-label="Page précédente"
                        >
                          <Icon name="chevronLeft" size={14} />
                        </button>
                        <span>
                          {previewPage} / {totalPreviewPages}
                        </span>
                        <button
                          type="button"
                          disabled={previewPage >= totalPreviewPages}
                          onClick={() => setPreviewPage((p) => p + 1)}
                          aria-label="Page suivante"
                        >
                          <Icon name="chevronRight" size={14} />
                        </button>
                      </nav>
                    </footer>
                  )}
                </div>
              </section>
            )}
          </main>
        </div>
      </div>

      {preview && (
        <footer className="dns-import-sticky" role="region" aria-label="Actions de débauche">
          <div className="dns-import-sticky__inner">
            <div className="dns-import-sticky__summary">
              <span className="dns-import-sticky__company">
                {selectedCompany?.name || 'Société'}
              </span>
              <span className="dns-import-sticky__detail">
                {actionableCount} adhérent(s) à débaucher
                {summary.foyerDependents ? ` · ${summary.foyerDependents} ayant(s)-droit de foyer` : ''}
              </span>
            </div>
            <div className="dns-import-sticky__actions">
              <button
                type="button"
                className="dns-import-btn dns-import-btn--ghost"
                onClick={clearFile}
                disabled={importing}
              >
                Annuler
              </button>
              <button
                type="button"
                className="dns-import-btn dns-import-btn--primary"
                onClick={handleImport}
                disabled={!canConfirm}
                title={actionableCount === 0 ? 'Aucun adhérent valide' : 'Débaucher la sélection'}
              >
                <Icon name="inbox" size={16} />
                {importing ? 'Débauche…' : `Débaucher ${actionableCount} adhérent(s)`}
              </button>
            </div>
          </div>
        </footer>
      )}

      {importing && (
        <div
          className="dns-import-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="deb-import-loading-title"
        >
          <div className="dns-import-overlay__card">
            <Spinner animation="border" variant="primary" />
            <h3 id="deb-import-loading-title">Débauche en cours</h3>
            <p>Archivage des adhérents — ne fermez pas cette page.</p>
            <div className="dns-import-overlay__bar" aria-hidden>
              <span />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ImportDebaucheView;
